import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LogIn, ArrowLeft, Key, Smartphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader } from '../ui/card';
import { getApiUrl } from '../../utils/api';
import { DeviceTrust } from '../../utils/deviceTrust';
import BabelmixLogo from '../BabelmixLogo';
import OTPVerificationModal from '../OTPVerificationModal';

const Login = ({ onUserLoggedIn, onBackToLanding }) => {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('sms'); // 'sms' or 'pin'
  const [pin, setPin] = useState('');
  const [hasPin, setHasPin] = useState(false);
  const [checkingPin, setCheckingPin] = useState(false);
  
  // OTP Verification State
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState({});

  // Check if user has PIN when phone number changes
  useEffect(() => {
    const checkPinStatus = async () => {
      if (phoneNumber.length >= 10) {
        setCheckingPin(true);
        try {
          const response = await axios.get(`${getApiUrl()}/auth/has-pin/${encodeURIComponent(phoneNumber)}`);
          setHasPin(response.data.has_pin);
        } catch (error) {
          setHasPin(false);
        }
        setCheckingPin(false);
      } else {
        setHasPin(false);
      }
    };
    
    const debounce = setTimeout(checkPinStatus, 500);
    return () => clearTimeout(debounce);
  }, [phoneNumber]);

  const handlePinLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const deviceInfoData = DeviceTrust.getDeviceInfo ? DeviceTrust.getDeviceInfo() : {};
      
      const response = await axios.post(`${getApiUrl()}/auth/pin-login`, {
        phone_number: phoneNumber,
        pin: pin,
        ...deviceInfoData
      });
      
      if (response.data.success) {
        localStorage.setItem('currentUser', JSON.stringify(response.data.user));
        onUserLoggedIn(response.data.user);
      }
    } catch (error) {
      console.error('PIN Login error:', error);
      const errorMessage = error.response?.data?.detail || t('auth.invalidPin', 'Invalid PIN');
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // If PIN mode is selected, use PIN login
    if (loginMode === 'pin') {
      return handlePinLogin(e);
    }
    
    setLoading(true);
    
    try {
      // Add device trust token to login
      const deviceInfoData = DeviceTrust.getDeviceInfo ? DeviceTrust.getDeviceInfo() : {};
      setDeviceInfo(deviceInfoData);
      
      // First check if verification is needed
      try {
        const verifyCheck = await axios.get(`${getApiUrl()}/auth/check-verification/${encodeURIComponent(phoneNumber)}`, {
          params: { device_token: deviceInfoData.device_token }
        });
        
        if (verifyCheck.data.needs_otp) {
          // Need OTP verification - show modal before completing login
          const loginResponse = await axios.post(`${getApiUrl()}/login`, {
            phone_number: phoneNumber,
            ...deviceInfoData
          });
          
          setPendingUser(loginResponse.data);
          setShowOTPModal(true);
          setLoading(false);
          return;
        }
      } catch (verifyErr) {
        console.log('Verification check failed, proceeding with normal login');
      }
      
      // Normal login flow
      const response = await axios.post(`${getApiUrl()}/login`, {
        phone_number: phoneNumber,
        ...deviceInfoData
      });
      
      console.log('🔐 Login with device token:', deviceInfoData.device_token);
      
      localStorage.setItem('currentUser', JSON.stringify(response.data));
      onUserLoggedIn(response.data);
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.detail || t('auth.userNotFound', 'User not found. Please register first.');
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle OTP verification success
  const handleOTPVerified = (result) => {
    console.log('OTP verified for login:', result);
    if (pendingUser) {
      // Mark user as verified and save
      const verifiedUser = { ...pendingUser, phone_verified: true };
      localStorage.setItem('currentUser', JSON.stringify(verifiedUser));
      console.log('Verified user logged in');
      setShowOTPModal(false);
      onUserLoggedIn(verifiedUser);
    }
  };
  
  // Handle OTP modal close (cancel verification)
  const handleOTPClose = () => {
    // Don't allow login without verification for new devices
    setShowOTPModal(false);
    setPendingUser(null);
    alert('Verifizierung abgebrochen. Bitte verifizieren Sie Ihr Gerät, um sich anzumelden.');
  };

  return (
    <div 
      className="h-full bg-gradient-to-br from-emerald-50 to-blue-50 flex items-start justify-center p-2 md:p-4 pt-2 overflow-y-auto" 
      style={{ height: '100%', minHeight: '100%' }}
    >
      <Card className="w-full max-w-md my-1">
        <CardHeader className="text-center py-2 md:py-4">
          {/* Back button */}
          {onBackToLanding && (
            <button
              onClick={onBackToLanding}
              className="absolute top-3 left-3 flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('common.back')}
            </button>
          )}
          <div className="flex justify-center mb-1">
            <BabelmixLogo size="medium" />
          </div>
          <p className="text-xs md:text-sm text-gray-600">{t('auth.enterPhone')}</p>
        </CardHeader>
        <CardContent className="px-3 md:px-6 py-2 md:py-4">
          <form onSubmit={handleLogin} className="space-y-2 md:space-y-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                {t('auth.phoneNumber')}
              </label>
              <Input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/\s+/g, ''))}
                placeholder="+49123456789"
                required
                className="text-sm md:text-base"
              />
            </div>
            
            {/* Login Mode Toggle - Only show if user has PIN */}
            {hasPin && (
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => setLoginMode('sms')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm transition-colors ${
                    loginMode === 'sms' 
                      ? 'bg-white text-emerald-700 shadow-sm font-medium' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  SMS
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMode('pin')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm transition-colors ${
                    loginMode === 'pin' 
                      ? 'bg-white text-emerald-700 shadow-sm font-medium' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Key className="w-4 h-4" />
                  PIN
                </button>
              </div>
            )}
            
            {/* PIN Input Field */}
            {loginMode === 'pin' && (
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                  {t('auth.loginPin', 'Login-PIN')}
                </label>
                <Input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="••••••"
                  maxLength={8}
                  required={loginMode === 'pin'}
                  className="text-sm md:text-base text-center tracking-widest"
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('auth.pinFromAdmin', 'PIN von Ihrem Administrator erhalten')}
                </p>
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading || (loginMode === 'pin' && pin.length < 4)}
            >
              {loginMode === 'pin' ? (
                <Key className="w-4 h-4 mr-2" />
              ) : (
                <LogIn className="w-4 h-4 mr-2" />
              )}
              {loading ? t('common.loading') : t('auth.login')}
            </Button>
            
            {/* Hint for PIN login */}
            {!hasPin && phoneNumber.length >= 10 && !checkingPin && (
              <p className="text-xs text-center text-gray-500">
                {t('auth.noPinSetup', 'Kein PIN eingerichtet? Fragen Sie Ihren Administrator.')}
              </p>
            )}
          </form>
        </CardContent>
      </Card>
      
      {/* OTP Verification Modal for new device */}
      <OTPVerificationModal
        isOpen={showOTPModal}
        onClose={handleOTPClose}
        phoneNumber={phoneNumber}
        deviceToken={deviceInfo.device_token}
        deviceType={deviceInfo.device_type}
        deviceName={deviceInfo.device_name}
        onVerified={handleOTPVerified}
        mode="verification"
      />
    </div>
  );
};

export default Login;
